import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { SUPPORTED_LANGUAGES } from '@/i18n';

const SITE_URL = 'https://funcomunion.es';
const DEFAULT_IMAGE = `${SITE_URL}/og-cover.jpg`;

const LOCALE_MAP: Record<string, string> = {
  es: 'es_ES',
  en: 'en_GB',
  it: 'it_IT',
  pt: 'pt_PT',
  'pt-BR': 'pt_BR',
  ro: 'ro_RO',
  fr: 'fr_FR',
  de: 'de_DE',
  pl: 'pl_PL',
  tl: 'tl_PH',
  sw: 'sw_KE',
};
function localeFor(code: string): string {
  return LOCALE_MAP[code] || 'es_ES';
}

interface SEOProps {
  // Cualquier ruta interna (sin dominio). Default '/'.
  path?: string;
  // Keys i18n para título y descripción
  titleKey?: string;
  descriptionKey?: string;
  // Override directo (sobrescribe las keys)
  title?: string;
  description?: string;
  // Imagen Open Graph (URL absoluta)
  image?: string;
  // Tipo OG (website, article, product)
  type?: 'website' | 'article' | 'product';
  // JSON-LD adicional (Product, Article, Review, etc.). LocalBusiness va
  // siempre en el layout, no se duplica aquí.
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  // No indexar (páginas privadas)
  noindex?: boolean;
}

export function SEO({
  path = '/',
  titleKey = 'seo.home.title',
  descriptionKey = 'seo.home.description',
  title,
  description,
  image = DEFAULT_IMAGE,
  type = 'website',
  jsonLd,
  noindex = false,
}: SEOProps) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] || 'es';

  const computedTitle = title || t(titleKey);
  const computedDescription = description || t(descriptionKey);

  // URL canónica path-based. ES en raíz, resto en /<lang>/...
  const buildUrl = (lang: string) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    if (lang === 'es') return `${SITE_URL}${cleanPath}`;
    // Quitar prefijo de idioma si está duplicado
    const stripped = cleanPath.replace(/^\/(en|it|pt)(\/|$)/, '/');
    return `${SITE_URL}/${lang}${stripped === '/' ? '/' : stripped}`;
  };
  const canonical = buildUrl(currentLang);

  // Sincronizar atributo lang del html (importante para Google y screen readers)
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = currentLang;
    }
  }, [currentLang]);

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{computedTitle}</title>
      <meta name="description" content={computedDescription} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical y hreflang */}
      <link rel="canonical" href={canonical} />
      {SUPPORTED_LANGUAGES.map(l => (
        <link key={l.code} rel="alternate" hrefLang={l.code} href={buildUrl(l.code)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={buildUrl('es')} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={computedTitle} />
      <meta property="og:description" content={computedDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={localeFor(currentLang)} />
      {SUPPORTED_LANGUAGES.filter(l => l.code !== currentLang).map(l => (
        <meta key={`alt-${l.code}`} property="og:locale:alternate" content={localeFor(l.code)} />
      ))}
      <meta property="og:site_name" content="Funcomunión" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={computedTitle} />
      <meta name="twitter:description" content={computedDescription} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD adicional para esta página */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// Schema.org LocalBusiness fijo. NOTA: el LocalBusiness principal ya está
// inline en index.html para que Google lo lea sin esperar JS. Esta función
// se mantiene exportada por si en algún momento se necesita un schema
// alternativo dinámico (ej. para una página de producto concreta).
export function LocalBusinessSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#funcomunion`,
    name: 'Funcomunión',
    description: 'Taller artesano de figuras coleccionables personalizadas para Primera Comunión. Diseño 3D, IA y pintura a mano.',
    url: SITE_URL,
    telephone: '+34 627 60 28 15',
    email: 'pedidos@funcomunion.es',
    image: DEFAULT_IMAGE,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Calle Caballeros, 8',
      addressLocality: 'Ciudad Real',
      addressRegion: 'Castilla-La Mancha',
      postalCode: '13001',
      addressCountry: 'ES',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 38.9852,
      longitude: -3.9270,
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'España' },
      { '@type': 'City', name: 'Ciudad Real' },
      { '@type': 'City', name: 'Albacete' },
      { '@type': 'City', name: 'Toledo' },
      { '@type': 'City', name: 'Cuenca' },
      { '@type': 'City', name: 'Madrid' },
    ],
    priceRange: '€€',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '10:00',
        closes: '14:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '17:00',
        closes: '20:30',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '10:30',
        closes: '14:00',
      },
    ],
    sameAs: [
      'https://www.instagram.com/funcomunion',
      'https://www.facebook.com/funcomunion',
      'https://www.tiktok.com/@funcomunion',
    ],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
